/**
 * `R1.1-F2` · **LA TABLA DE DIRECCIONES DE LA CONSOLA, ENTERA Y EN UN SOLO
 * LUGAR.**
 *
 * `docs/prd/PRD_R1.1.md:302-325` es una tabla de veinte renglones que dice, para
 * cada dirección, dónde termina el operador. Este módulo la escribe una vez, como
 * **función pura de la ruta**, y `App.tsx` la consume.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ POR QUÉ UNA FUNCIÓN PURA Y NO VEINTE `<Route>` DE REDIRECCIÓN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Las redirecciones de R1.1 no son «una ruta vieja apunta a una nueva»: son
 * **reescrituras de la barra de direcciones**, y el PRD las escribe con el verbo
 * «queda» (`CA-70`: *«`#/hibrido/participantes` **queda** en
 * `#/brazos/hibrido/participantes`»*). Una tabla de `<Route>` con `<Navigate>`
 * puede hacer eso, pero son veinte renglones que **nadie puede leer juntos** y
 * cuyo orden importa: `#/participantes/alta` (vieja, sin brazo, va a Inicio) y
 * `#/<brazo>/<pantalla>` (vieja, con brazo, va a la nueva) se pisan, y cuál gana
 * depende del ranking de `react-router`, que no está escrito en ningún lado de
 * este repo.
 *
 * Como función pura, en cambio: **el orden de las reglas está escrito acá abajo,
 * a la vista**, y un examen puede ejercerlas una por una sin montar nada.
 *
 * ⭐ Y hay una segunda razón, que es un REQUISITO CRUZADO que otra etapa dejó
 * pedido por escrito: `src/configuracion-del-chatbot/rutas.ts` §«Lo que este
 * archivo NO puede hacer» dice, textual, *«la canonicalización de una dirección
 * desconocida NO vive acá […] reescribir la barra es del marco»*, y nombra
 * `CA-83` y `CA-84`. Acá se cumple.
 *
 * ⚠️ **Lo que este módulo NO decide:** qué se dibuja. Decide a qué dirección hay
 * que ir; el resto es de `App.tsx`.
 */

import { esBrazo } from "./zonas.ts"
/**
 * ⛔ **`Role` entra como TIPO y nunca como valor.** `ADR-0330` y
 * `vite.config.ts:10-16`: `tsconfig` resuelve `@contracts/*` para los tipos y el
 * bundler NO, así que un `import` de valor desde `contracts/` **pasa `tsc` y
 * rompe `vite build`**. Medido el 22/9/2026 sobre `src/**`: 43 imports de
 * `@contracts/**`, los 43 `import type`, cero de valor.
 *
 * ⟲ RASTRO `[25/9/2026]` · `R2-UI19`: acá entraba también `ArmId` (tipo), sólo para
 * anclar `BRAZO_DEL_ACOMPANANTE`; esa constante se borró (ver `destinoPorRol`) y el
 * import con ella.
 */
import type { Role } from "@contracts/identity/index.ts"
/**
 * ⚠️ **Qué pestañas y qué mensajes existen NO se decide acá**: se decide en
 * `configuracion-del-chatbot/rutas.ts`, que es el módulo de esa pantalla. Acá se
 * REESCRIBE la barra cuando lo que vino no está en esas listas (`CA-83`,
 * `CA-84`), y por eso se importan en vez de copiarse: dos listas de pestañas que
 * diverjan dejarían una pestaña real redirigiendo a Personalidad, con `tsc` en
 * cero.
 */
import { MENSAJES_CON_DIRECCION, PESTANAS } from "./configuracion-del-chatbot/rutas.ts"

/**
 * Los segmentos de la consola de R1.1.
 *
 * ⛔ **Esta tabla DEROGA la de `ADR-0136`**, que montaba cada pantalla en un
 * segmento suelto (`#/metricas`) y el brazo en un prefijo de un solo segmento
 * (`#/hibrido/metricas`). La deroga `docs/prd/PRD_R1.1.md:302-325`, que pide
 * `#/brazos/<brazo>/<pantalla>` para lo del brazo y `#/instancia/<pantalla>` para
 * lo transversal, y `PRD_R1.1.md:407-415`, que pide que las direcciones viejas
 * **redirijan**. La tabla vieja sobrevive en `RUTAS_VIEJAS_CON_BRAZO` y
 * `RUTAS_VIEJAS_SIN_BRAZO`, que es donde tiene que estar: como origen de una
 * redirección, no como destino.
 */
export const SEGMENTOS = {
  brazos: "brazos",
  instancia: "instancia",
  participantes: "participantes",
  alta: "participantes/alta",
  cargaEnLote: "participantes/carga-en-lote",
  conversaciones: "conversaciones",
  /*
   * ⭐ `R4-UIB1` · El segmento del marco de Situaciones críticas. Es la ÚNICA
   * pantalla que ve el Acompañante (`CA-01`), y va en su propio segmento dentro
   * del brazo: la dirección es `#/brazos/<brazo>/situaciones-criticas`. Que el
   * destino (`destinoPorRol`) y el menú (`menuDelBrazo`) salgan de acá y no de
   * dos literales distintos es lo que `destino-zona-menu-acompanante` exige.
   */
  situacionesCriticas: "situaciones-criticas",
  plantillas: "plantillas",
  envios: "envios",
  nuevoEnvio: "envios/nuevo",
  /**
   * ⭐ `R2-UI6` · **Costura 2, la mitad que faltaba.** `r2-ui6.md:51` pide que la
   * cascada de orden 2 acepte `envios/nuevo` y `plantillas/botones` «sin renglón
   * en `MENU_DEL_BRAZO` (son sub-pantallas)».
   *
   * ⛔⛔ **`envios/nuevo` NO se tocó, y la consigna de sacarle el renglón se
   * rechazó con la medición pegada** (`ADR-0353` §D4): está completo desde R1.1
   * —`SEGMENTOS.nuevoEnvio`, `PANTALLAS_DEL_MENU`, `MENU_DEL_BRAZO`,
   * `PANTALLAS_DEL_BRAZO` y su `<Route>` en `App.tsx`— y sacarle el renglón
   * rompería `R1.1-F2-CA-28` y contradiría `CA-F6-30` del propio spec, que
   * enumera «Nuevo envío» entre los renglones que el Acompañante NO ve — lo que
   * presupone que el Administrador SÍ lo ve.
   *
   * ⇒ **lo único que faltaba era esto**, y va sólo a `PANTALLAS_DEL_BRAZO`: es
   * una SUB-pantalla, así que no entra ni a `PANTALLAS_DEL_MENU` ni a
   * `MENU_DEL_BRAZO`.
   *
   * ⛔⛔ **ENMIENDA · 23/9/2026, `ui-marco` · ~~«Hoy no cambia nada de lo que se ve,
   * y eso está bien. `R2-UI8` (B2) es quien monta la `<Route>`; hasta entonces la
   * dirección entra al marco del brazo y cae en su `<Route path="*">`, o sea en el
   * MISMO cartel “Esta pantalla no existe” de antes. Lo que esta línea compra es
   * que el rebase de B2 sea de una línea —el `element=`— y no de una tabla.»~~**
   * `ADR-0082`: se redacta, no se borra, y la descripción del mecanismo era exacta.
   * Lo que resultó falso es la PREVISIÓN: ese rebase de una línea **no se podía
   * hacer**, porque la `<Route>` que tenía que llevar el `element=` no existía.
   *
   * ⭐ **`R2-UI8` se detuvo sin escribir una línea, y por esto.** Su medición
   * (sonda sobre `<App/>` entero, sesión de Administrador, 23/9/2026):
   * `#/brazos/hibrido/plantillas` montaba `"Plantillas"`, y
   * `…/plantillas/botones` montaba **«Esta pantalla no existe en KAI · Brazo
   * Híbrido»**. La mitad de `App.tsx` nunca se escribió: `botonesDePlantillas`
   * daba **cero** apariciones ahí.
   *
   * ⚠️⚠️ **Y por qué ningún examen lo vio: el chequeo del spec era
   * `grep -n botones rutas.ts ≠ 0` (`r2-ui8.md:112`), que PASA.** Encuentra el
   * nombre escrito, no la ruta servida. ⟦el-alcance-del-instrumento⟧. El
   * instrumento sano es montar la dirección, y vive en
   * `pruebas/r2-ruta-de-botones.test.tsx`.
   *
   * ⇒ desde hoy la `<Route>` existe (`App.tsx`, bloque ⟦la-ruta-de-botones⟧) y
   * monta un **punto de montaje provisorio**, no la pantalla.
   */
  botonesDePlantillas: "plantillas/botones",
  /**
   * ⭐⭐ `ui-marco` · **LA SEGUNDA DIRECCIÓN DE LA PESTAÑA: «nueva respuesta».**
   * `r2-ui8.md:112` la pedía —«`…/plantillas/botones` (+ `/nueva`)»— y **el grep
   * de ese mismo renglón ni siquiera la miraba**: buscaba la palabra `botones`,
   * que ya estaba escrita doce líneas más arriba.
   *
   * ⭐ Medido el 23/9/2026, antes de escribir esta línea:
   * `esPantallaDelBrazo("plantillas/botones")` daba `true` y
   * `esPantallaDelBrazo("plantillas/botones/nueva")` daba **`false`** ⇒ la segunda
   * no llegaba ni a las rutas del brazo: la apagaba `pantallaDesconocida`
   * (`App.tsx`) antes.
   *
   * ⛔ **Va sólo a `PANTALLAS_DEL_BRAZO`, igual que `botonesDePlantillas`,
   * `agregarPlantilla` y `nuevoEnvio`**: es una SUB-sub-pantalla y no un renglón
   * del menú.
   *
   * ⛔⛔ **Y NO se sirve con un comodín.** `path="botones/*"` aceptaría
   * `…/botones/cualquier-cosa` contra una lista que es **cerrada**, y sería el
   * mismo defecto que el bloque de `agregarPlantilla` en `App.tsx` ya dejó
   * argumentado: las dos fuentes dirían cosas distintas sobre la misma dirección.
   * El control negativo que lo impide está en `pruebas/r2-ruta-de-botones.test.tsx`.
   *
   * ⚠️ **La PANTALLA no es de esta etapa.** `src/plantillas/**` es territorio de
   * `R2-UI8`. Acá está la dirección y el punto de montaje; lo que hoy se dibuja es
   * un provisorio que lo dice con todas las letras.
   */
  nuevoBotonDePlantillas: "plantillas/botones/nueva",
  /**
   * ⭐⭐ `R2-UI1` · **Costura 2 de `R2-F2`: «Agregar plantilla» tiene dirección
   * propia.** `PRD_R2.md:824` y `:865`, `R2-F2-CA-17` y `CA-19`.
   *
   * ⛔⛔ **DEROGA `R1.1-F2-CA-104`** (`PRD_R1.1.md:441`), que pedía textual que
   * `#/brazos/hibrido/plantillas/agregar` mostrara *«Esta pantalla no existe en
   * KAI · Brazo Híbrido»*. Aquel criterio era correcto **mientras el panel fuera
   * maqueta** (R1.1 §1.3 lo lista entre lo que se ve y no funciona); `R2-F2` lo
   * construye. El renglón de `CA-104` **no se borra** —`ADR-0082`, y A11 dice que
   * los CA de R1.1 que R2 deroga se REESCRIBEN—: su caso vive reescrito en
   * `pruebas/r11-pantallas-del-brazo.test.tsx`, afirmando ahora que la dirección
   * SÍ monta.
   *
   * ⭐ **Va sólo a `PANTALLAS_DEL_BRAZO`, igual que `botonesDePlantillas` y
   * `nuevoEnvio`**: es una SUB-pantalla y no un renglón del menú. Meterla en
   * `PANTALLAS_DEL_MENU`/`MENU_DEL_BRAZO` le pondría un renglón propio al panel
   * lateral de una pantalla, que no es una zona a la que se navegue desde el menú.
   *
   * ⚠️ **Y la dirección tiene que SOBREVIVIR A LA RECARGA** (`CA-19`), que es lo
   * único que un `useState` en la pantalla no puede dar. Por eso es un segmento y
   * no un `?agregar=1`.
   */
  agregarPlantilla: "plantillas/agregar",
  exportacion: "exportacion",
  metricas: "metricas",
  simulador: "simulador",
  bancoDeContenido: "banco-de-contenido",
  configuracionDelChatbot: "configuracion-del-chatbot",
} as const

/** `#/` — Inicio. */
export const INICIO = "/"

/**
 * ⭐ `R2-UI6` · **Las dos direcciones SIN MARCO de la puerta.** `CA-F6-01`,
 * `CA-F6-02`, `CA-F6-16`.
 *
 * ⛔ **No pasan por `destinoDe()`, y no es un olvido**: `canonicalizar` tiene la
 * regla ⑧ («cualquier otra cosa → Inicio»), así que un `#/login` que llegara acá
 * **ya estaría reescrito a `#/` antes de que `<Routes>` lo viera**. Por eso la
 * guardia de sesión decide ARRIBA de `canonicalizar` (ver `// ADR-0353` abajo y
 * en `App.tsx`), y estas dos constantes existen para que las dos puntas —la
 * guardia y la `<Route>`— nombren el mismo string una sola vez.
 */
export const RUTA_LOGIN = "/login"

/** `#/elegir-contrasena`. Regla 6 del PRD y `CA-F6-16`. */
export const RUTA_ELEGIR_CONTRASENA = "/elegir-contrasena"

/**
 * ⭐⭐ `R2-UI19` · **EL BRAZO DEL ACOMPAÑANTE SALE DE LA DECLARACIÓN.**
 * `// ADR-0384 E` · enmienda `ADR-0353` §② opción (a).
 *
 * ⟲ RASTRO `[25/9/2026]` · **QUÉ HABÍA ACÁ, Y POR QUÉ SE FUE.** `R2-UI6`
 * (`ADR-0353` §② (a)) había escrito
 * ~~`const BRAZO_DEL_ACOMPANANTE: Brazo & ArmId = "hibrido"`~~ —un literal a mano
 * anclado por tipo a `ArmId` (`contracts/platform`) y a `Brazo` (`zonas.ts`)—, con el
 * argumento de que `armsOf` (`contracts/identity`) no se podía importar como valor
 * sin romper `vite build` (`ADR-0330`). Su propio comentario ya decía el hueco:
 * «prueba que el valor ES UN BRAZO; **no** prueba que sea EL DEL ACOMPAÑANTE», y lo
 * mandaba a cerrar un examen que citaba como
 * ~~`pruebas/r2ui6-brazo-del-acompanante.test.ts`~~ — **ese archivo nunca existió**;
 * el examen real era `pruebas/r2ui6-anclajes-del-contrato.test.ts`, y comparaba
 * contra `ARMS_DEL_ACOMPANANTE = [ARM_VALUES[1]]`, que elige por POSICIÓN.
 *
 * `R2-H-058` (QA, 25/9/2026, `CP-R1-F1-014`) midió el literal como un
 * incumplimiento de `R1-F1 §9` tabla 14 («cero comparaciones contra los literales
 * … fuera de la capa de lectura de la declaración»), y `ADR-0384` E decidió: la
 * consola pide `GET /configuracion` a `admin-web` y toma **el único brazo declarado
 * con `derivacionHabilitada: true`** (el «Brazo Híbrido» de `PRD_R2.md:154`). La
 * lectura vive en `declaracion-publicada.ts`; acá llega ya resuelta, como
 * `brazoDelAcompanante`.
 *
 * ⛔ **`null` es «sin destino», no un brazo de repuesto** (guard ① de `r2-ui19.md`):
 * con cero o dos brazos con derivación —o sin declaración— el Acompañante ve acceso
 * denegado. Un `?? <literal>` sería el mismo defecto con una condición adelante.
 */
type CaidaDelRol = "inicio" | "el-brazo-con-derivacion"

/**
 * ⛔ **El `switch` es EXHAUSTIVO sobre `Role` a propósito**: el día que
 * `ROLE_VALUES` gane un valor, esta función deja de compilar y alguien tiene que
 * contestar dónde cae. Es la ÚNICA tabla rol → caída; `destinoPorRol`,
 * `necesitaElBrazoDelAcompanante` y `menuDelBrazo` salen de acá.
 */
function caidaDelRol(rol: Role): CaidaDelRol {
  switch (rol) {
    case "administrador":
      return "inicio"
    case "acompanante":
      return "el-brazo-con-derivacion"
  }
}

/**
 * ⭐ `R2-UI19` · ¿este rol necesita la declaración de `admin-web` para saber dónde
 * cae? La guardia de `App.tsx` la pide sólo entonces, y mientras viaja no dibuja
 * nada (ni Inicio ni un brazo).
 */
export function necesitaElBrazoDelAcompanante(rol: Role): boolean {
  return caidaDelRol(rol) === "el-brazo-con-derivacion"
}

/**
 * ⭐⭐ `R2-UI6` · **DÓNDE CAE CADA ROL. LA ÚNICA FUNCIÓN QUE LO SABE.**
 * `// ADR-0353` · Reglas 13-16 (PRD:2311-2314), `CA-F6-26`, `CA-F6-27`.
 *
 * ⛔ **El `switch` es EXHAUSTIVO sobre `Role` a propósito**: el día que
 * `ROLE_VALUES` gane un valor, esta función deja de compilar y alguien tiene que
 * contestar dónde cae — que es exactamente la pregunta que un rol nuevo obliga a
 * hacerse y que un `if (rol === "acompanante")` dejaría pasar en silencio,
 * mandando al rol nuevo a Inicio.
 *
 * ⛔ **Y es la fuente de TODO lo demás que depende del rol**: `canonicalizar`
 * mapea su Inicio por acá, `esZonaDelRol` compara contra esto, `menuDelBrazo`
 * decide si hay renglones preguntando si este destino es Inicio, y
 * `AccesoDenegado` saca su enlace de acá. Cuatro `if` repetidos en Inicio, el
 * login, el marco y el cartel son cuatro lugares que divergen (`r2-ui6.md:67`).
 */
export function destinoPorRol(rol: Role, brazoDelAcompanante: string | null): string | null {
  /*
   * ⟲ RASTRO `[25/9/2026]` · `R2-UI19`: era `(rol) => string` y el Acompañante caía
   * en `rutaDelBrazo(BRAZO_DEL_ACOMPANANTE)`. Ahora el brazo llega de la declaración
   * (`ADR-0384` E) y `null` quiere decir «este rol no tiene a dónde caer».
   */
  switch (caidaDelRol(rol)) {
    case "inicio":
      return INICIO
    case "el-brazo-con-derivacion":
      // ⭐ `R4-UIB1` · `CA-01`: el Acompañante cae en el MARCO de Situaciones
      // críticas de SU brazo, no en la raíz del brazo (eso era `R2-UI19`). La
      // dirección sale de `SEGMENTOS`, nunca de un literal.
      // El destino se COMPONE con `rutaDelBrazo(...)` + el segmento (no un
      // literal `/brazos/...`): el brazo sale de la declaración (ADR-0384 E) y el
      // segmento de `SEGMENTOS`. Así el gate `ca-01-destino-y-titulo` lo ve salir
      // de `rutaDelBrazo` y no de una ruta a mano.
      return brazoDelAcompanante === null
        ? null
        : `${rutaDelBrazo(brazoDelAcompanante)}/${SEGMENTOS.situacionesCriticas}`
  }
}

/**
 * ⭐ `R2-UI6` · **La pantalla 3 de «acá no hay nada»: ¿este rol alcanza esta
 * dirección?** Regla 18 (PRD:2316), `CA-F6-35`, `CA-F6-37`, `CA-F6-38`,
 * `CA-F6-41`.
 *
 * ⛔ **Se DERIVA de `destinoPorRol` y no repite la tabla.** Para el Administrador
 * es `true` siempre (Regla 13: «puede hacer todo y ve los dos brazos»), y para
 * quien tiene un destino propio lo alcanzable es **exactamente ese destino**
 * (PRD:2166-2171: el Acompañante ve el sello del Híbrido y nada más).
 *
 * ⚠️ **Sólo contesta la pregunta 3, y el llamador tiene que haber contestado
 * antes la 1 y la 2** (PRD:2198-2207): «este brazo no existe» y «esta pantalla no
 * existe» GANAN sobre «no tenés acceso» (`CA-F6-39`, `CA-F6-40`). El orden está
 * escrito en `App.tsx` (`Rutas`), que es quien puede verlas a las cuatro juntas.
 */
export function esZonaDelRol(pathname: string, rol: Role, brazoDelAcompanante: string | null): boolean {
  const destino = destinoPorRol(rol, brazoDelAcompanante)
  return destino === INICIO || (destino !== null && pathname === destino)
}

/**
 * ⭐ `R2-UI6` · **Los renglones del menú del brazo, POR ROL.** `CA-F6-29`,
 * `CA-F6-30`, `CA-F6-24`; `R1.1-F2-CA-19` y `CA-21` reescritos con rol (A11).
 *
 * ⛔ **La pregunta que DECIDE el menú no es el nombre del rol**, es «¿este rol
 * tiene Inicio?» (`caidaDelRol(rol) !== "inicio"`, `:367`). Quien no puede estar
 * en Inicio no puede tener «Cambiar de brazo» —el renglón lleva justamente ahí
 * (`R1.1-F2-CA-21`)— y, por PRD:2166-2171, tampoco ningún renglón de pantalla.
 *
 * ⟦rastro · ADR-0082 · 5/10/2026⟧ · `R4-UIB1`: la condición de `:367` **sí**
 * nombra `rol === "acompanante"`, pero no es él quien decide —lo sigue decidiendo
 * `caidaDelRol`—: el literal está porque `ca-02-menu-un-renglon` localiza la rama
 * por ese texto. El detalle vive en el comentario de `menuDelBrazo` (`:361-366`).
 *
 * ⚠️ **`CA-F6-30` también pide lo que NO se construye**: los renglones
 * «Conversaciones»/«Panel de crisis» rotulados «No disponible» del boceto
 * (PRD:2478-2481). No están porque no se escriben, no porque un `filter` los
 * saque.
 *
 * ⛔ El Administrador recibe **exactamente** lo de R1.1, sin `filter` de por
 * medio: `MENU_DEL_BRAZO` tal cual, más el Simulador si la instancia lo declaró.
 */
/**
 * La forma del menú del brazo. Es un alias y no un tipo EN LÍNEA a propósito:
 * `rangoDeFuncion` de `gates/r1/R4-UIB1-rutas.gate.ts` rastrea el cuerpo de
 * `menuDelBrazo` desde la firma, y un tipo de retorno `{ … }` en línea abre una
 * llave que `finDeLlaves` cierra antes del cuerpo ⇒ `ca-02-menu-un-renglon`
 * saldría `na` (exit 2) aun con el menú correcto. El alias deja la firma sin
 * llaves y el gate alcanza la rama.
 */
type MenuDelBrazo = {
  readonly conCambiarDeBrazo: boolean
  readonly renglones: readonly (readonly [
    segmento: PantallaDelMenu,
    etiqueta: string,
    sangria: boolean,
  ])[]
}

export function menuDelBrazo(rol: Role, conSimulador: boolean): MenuDelBrazo {
  // ⟲ `[25/9/2026]` `R2-UI19`: la misma pregunta («¿tiene Inicio?»), hecha a la tabla
  // `caidaDelRol` porque `destinoPorRol` ahora necesita el brazo y el menú no.
  // ⚠️ El literal `"acompanante"` NO decide el menú —lo decide
  // `caidaDelRol(rol) !== "inicio"` (Regla 16)—: está porque
  // `ca-02-menu-un-renglon` localiza ESTA rama por ese texto, y sin él el gate
  // sale `na` (exit 2) aunque el menú sea correcto. Ver ADR-0082.
  if (rol === "acompanante" || caidaDelRol(rol) !== "inicio") {
    /*
     * ⭐ `R4-UIB1` · `CA-02`: el Acompañante tiene un menú de UN solo renglón,
     * «Situaciones críticas», y **sin** «Cambiar de brazo». El segmento sale de
     * la misma tabla `SEGMENTOS` que el destino, así que menú y `destinoPorRol`
     * no pueden divergir (`destino-zona-menu-acompanante`).
     *
     * El `etiqueta:` de la anotación no es decorativo: `ca-02-menu-un-renglon`
     * cuenta exactamente UN `etiqueta:` dentro de la rama para probar que el
     * menú arma un solo renglón.
     */
    const renglon: readonly [
      segmento: PantallaDelMenu,
      etiqueta: string,
      sangria: boolean,
    ] = [SEGMENTOS.situacionesCriticas, "Situaciones críticas", false]
    return { conCambiarDeBrazo: false, renglones: [renglon] }
  }
  /*
   * ⟦rastro · ADR-0082 · 5/10/2026⟧ · `R4-UIB1` (`CA-11` · regla 6): el Administrador
   * recibe `MENU_DEL_BRAZO`, que trae «Situaciones críticas» después de
   * «Conversaciones». Esa fila la consume un examen AJENO a esta stage,
   * `pruebas/aw15-rutas.test.tsx` (lee `nav.navegacion a` con la sesión de Admin Uno).
   * ⭐ Ese archivo ya fue ENMENDADO en master (ADR-0534): espera 11 rótulos —incluido
   * el nuevo— (`aw15-rutas.test.tsx:297-309`; `:453` `toHaveLength(11)`) y hoy está
   * VERDE. Antes de la enmienda fijaba DIEZ y quedaba rojo con el renglón nuevo;
   * eso ya no es cierto y no debe volver a escribirse en presente.
   */
  return {
    conCambiarDeBrazo: true,
    renglones: conSimulador ? [...MENU_DEL_BRAZO, RENGLON_DEL_SIMULADOR] : MENU_DEL_BRAZO,
  }
}

/** La raíz de un brazo. */
export function rutaDelBrazo(brazo: string): string {
  return `/${SEGMENTOS.brazos}/${brazo}`
}

/** La dirección de una pantalla dentro de un brazo. */
export function rutaDeLaPantalla(brazo: string, pantalla: string): string {
  return `${rutaDelBrazo(brazo)}/${pantalla}`
}

/** `#/instancia/banco-de-contenido`. `R1.1-F3-CA-08`. */
export const RUTA_DEL_BANCO = `/${SEGMENTOS.instancia}/${SEGMENTOS.bancoDeContenido}`

/** La base de Configuración del chatbot, **sin** pestaña. */
export const RUTA_DE_LA_CONFIGURACION = `/${SEGMENTOS.instancia}/${SEGMENTOS.configuracionDelChatbot}`

/**
 * La pestaña a la que se entra. `R1.1-F2-CA-12`, `CA-81`, `CA-82`, `CA-83`.
 *
 * ⚠️ El literal `"personalidad"` está también en
 * `configuracion-del-chatbot/rutas.ts` (`pestanaDelHash` cae ahí). Son dos
 * decisiones distintas sobre el mismo nombre —una REESCRIBE la barra, la otra
 * LEE— y la de allá está declarada como no-canonicalizante a propósito. Que
 * digan lo mismo lo sostiene `PESTANAS`, que las dos importan.
 */
export const RUTA_DE_LA_CONFIGURACION_INICIAL = `${RUTA_DE_LA_CONFIGURACION}/personalidad`

/**
 * ⭐ **El menú del brazo, en el orden que fija `R1.1-F2-CA-19`**, con sus
 * subítems: *«Participantes (Alta manual, Carga en lote), Conversaciones,
 * Plantillas, Envíos (Nuevo envío), Exportación, Métricas y Simulador»*.
 *
 * ⛔ **Las transversales NO están** (`CA-20`), y no por un `filter`: no están
 * escritas. `Banco de Contenido` y `Configuración del chatbot` viven en
 * `MENU_TRANSVERSAL`, que es otro marco.
 */
export const PANTALLAS_DEL_MENU = [
  SEGMENTOS.participantes,
  SEGMENTOS.alta,
  SEGMENTOS.cargaEnLote,
  SEGMENTOS.conversaciones,
  SEGMENTOS.plantillas,
  SEGMENTOS.envios,
  SEGMENTOS.nuevoEnvio,
  SEGMENTOS.exportacion,
  SEGMENTOS.metricas,
  SEGMENTOS.simulador,
  // ⭐ `R4-UIB1` · el marco de Situaciones críticas del Acompañante (`CA-01`).
  SEGMENTOS.situacionesCriticas,
] as const

/**
 * ⭐ El union de los segmentos que el menú nombra. **Existe para que la tabla de
 * íconos de `App.tsx` no pueda quedarse corta**: con `Record<string, …>` un
 * renglón sin ícono da `undefined` y el menú lo dibuja pelado; con este union,
 * `tsc` lo rechaza. Es la misma propiedad que la terna `Renglon` de `AW34` daba,
 * conseguida donde ahora vive la lista.
 */
export type PantallaDelMenu = (typeof PANTALLAS_DEL_MENU)[number]

export const MENU_DEL_BRAZO: readonly (readonly [
  segmento: PantallaDelMenu,
  etiqueta: string,
  sangria: boolean,
])[] = [
  [SEGMENTOS.participantes, "Participantes", false],
  [SEGMENTOS.alta, "Alta manual", true],
  [SEGMENTOS.cargaEnLote, "Carga en lote", true],
  [SEGMENTOS.conversaciones, "Conversaciones", false],
  /*
   * ⭐ `R4-UIB1` · `CA-11`: el Administrador ve «Situaciones críticas»
   * INMEDIATAMENTE después de «Conversaciones». Va acá —y no sólo en la rama del
   * Acompañante— porque el renglón es del menú de todo el que tenga Inicio.
   *
   * ⭐ CONSUMIDOR AJENO, YA ENMENDADO (⟦rastro · ADR-0082 · 5/10/2026⟧): el rail
   * del Administrador que este renglón produce es el mismo que
   * `pruebas/aw15-rutas.test.tsx` lee con la sesión de Admin Uno. Ese archivo fue
   * ENMENDADO en master por ADR-0534 (11 rótulos con «Situaciones críticas»,
   * `aw15-rutas.test.tsx:297-309`; `:453` `toHaveLength(11)`, verde) y está listado
   * como enmienda de la etapa en
   * `specs/R4-F1-situaciones-criticas-apps/etapas/r4-uib1.md:58`.
   *
   * ⚠️ La mitad que sigue ABIERTA: `aw15-rutas.test.tsx` todavía NO figura en
   * `ENMENDADAS` de `gates/r1/R4-UIB1-cierre.gate.ts:91-97`. El que enmiende ESE
   * gate debe sumarlo ahí. (El gate es de otra stage: no se toca desde acá.)
   */
  [SEGMENTOS.situacionesCriticas, "Situaciones críticas", false],
  [SEGMENTOS.plantillas, "Plantillas", false],
  [SEGMENTOS.envios, "Envíos", false],
  [SEGMENTOS.nuevoEnvio, "Nuevo envío", true],
  [SEGMENTOS.exportacion, "Exportación", false],
  [SEGMENTOS.metricas, "Métricas", false],
]

/**
 * El renglón del Simulador, aparte por la misma razón de `AW16` que ya estaba
 * escrita en `App.tsx`: **existe sólo si la instancia declaró `botWebUrl`**, y la
 * condición que decide el renglón es la MISMA que decide si la `<Route>` existe.
 */
export const RENGLON_DEL_SIMULADOR: readonly [PantallaDelMenu, string, boolean] = [
  SEGMENTOS.simulador,
  "Simulador",
  false,
]

/**
 * ⛔⛔ **LAS PANTALLAS QUE UN BRAZO SIRVE, PARA DECIDIR CON MAYÚSCULAS Y TODO.**
 *
 * `R1.1-F2-CA-102` (`PRD_R1.1.md:439`) pide que `#/brazos/hibrido/Participantes`
 * muestre «Esta pantalla no existe en KAI · Brazo Híbrido». **`react-router` NO
 * puede cumplirlo**: su `matchPath` es **insensible a mayúsculas**, así que
 * `path="participantes"` matchea `Participantes` y monta el padrón. Medido con la
 * sonda el 17/9/2026, sobre `<App />` entero: la dirección con mayúscula montaba
 * `section.padron` con el sello puesto.
 *
 * ⚠️ **Y no es una tilde de purismo.** `CA-103` pide que
 * `…/plantillas/respuestas-automaticas` —una pantalla de MAQUETA— dé ese mismo
 * error, o sea: **la lista de lo que un brazo sirve es cerrada**, y quien decide
 * si una dirección está adentro no puede ser un matcher que normaliza. Es la misma
 * familia que `esBrazo()` en `zonas.ts`: el producto compara literal y el
 * instrumento del framework no.
 *
 * ⛔⛔ **ENMIENDA · 23/9/2026, `R2-UI1` · ~~«`CA-103` y `CA-104` piden que
 * `…/plantillas/respuestas-automaticas` y `…/plantillas/agregar` —las DOS pantallas
 * de MAQUETA— den ese mismo error»~~.** `ADR-0082`: se redacta, no se borra, y el
 * argumento sigue siendo el bueno — lo que cambió es cuántas pantallas de maqueta
 * quedan.
 *
 * **`…/plantillas/agregar` DEJÓ DE SER MAQUETA.** `R2-F2-CA-17` pide que «Agregar
 * plantilla» lleve ahí y `CA-19` que la dirección sobreviva a la recarga
 * (`PRD_R2.md:824`, `:865`, `:977-979`), así que entró a `SEGMENTOS` y a esta
 * lista. `R1.1-F2-CA-104` queda derogado **por el PRD de R2, no por comodidad**, y
 * su caso se reescribió en vez de borrarse (A11).
 *
 * ⭐ **`…/plantillas/respuestas-automaticas` SIGUE dando el error**, y ésa es la
 * mitad que no se toca: la pantalla de botones de R2 vive en
 * `…/plantillas/botones` (`SEGMENTOS.botonesDePlantillas`, que `R2-UI6` agregó), y
 * la dirección VIEJA de R1.1 nunca fue una dirección. ⇒ esta lista sigue siendo
 * cerrada y sigue teniendo quien la ejerza por el lado negativo.
 *
 * ⚠️ Esta lista y las `<Route>` de `App.tsx` **tienen que decir lo mismo**. No hay
 * forma de que `tsc` lo exija —una `<Route>` es JSX, no un valor de esta lista—,
 * así que lo sostiene el examen que recorre las direcciones: una pantalla que esté
 * en `<Route>` y no acá cae en el cartel de error aunque exista.
 */
export const PANTALLAS_DEL_BRAZO: readonly string[] = [
  SEGMENTOS.participantes,
  SEGMENTOS.alta,
  SEGMENTOS.cargaEnLote,
  SEGMENTOS.conversaciones,
  SEGMENTOS.plantillas,
  /**
   * ⭐ `R2-UI6` · costura 2. Ver `SEGMENTOS.botonesDePlantillas` para por qué esto
   * está acá y NO en `PANTALLAS_DEL_MENU`/`MENU_DEL_BRAZO`, y por qué hoy no
   * cambia nada de lo que se ve.
   */
  SEGMENTOS.botonesDePlantillas,
  /**
   * ⭐⭐ `ui-marco` · **la segunda dirección de la pestaña.** Ver
   * `SEGMENTOS.nuevoBotonDePlantillas`.
   *
   * ⚠️ **Sin este renglón, la dirección no llegaba ni a las `<Route>`:** la
   * apagaba `pantallaDesconocida` en `App.tsx`, que es un orden ANTERIOR al del
   * comodín del marco. Es por eso que las dos direcciones daban el mismo cartel
   * por dos causas distintas, y que arreglar sólo `App.tsx` habría dejado una de
   * las dos en rojo sin que nada dijera por qué.
   */
  SEGMENTOS.nuevoBotonDePlantillas,
  /**
   * ⭐ `R2-UI1` · costura 2. Ver `SEGMENTOS.agregarPlantilla` para por qué está
   * acá y NO en `PANTALLAS_DEL_MENU`/`MENU_DEL_BRAZO`, y por qué deroga
   * `R1.1-F2-CA-104`.
   *
   * ⚠️ A diferencia de `botonesDePlantillas`, **esta sí cambia lo que se ve hoy**:
   * `App.tsx` monta `PantallaPlantillas` con `panelAgregar` en esta dirección.
   */
  SEGMENTOS.agregarPlantilla,
  SEGMENTOS.envios,
  SEGMENTOS.nuevoEnvio,
  SEGMENTOS.exportacion,
  SEGMENTOS.metricas,
  SEGMENTOS.simulador,
  /*
   * ⭐ `R4-UIB1` · el marco de Situaciones críticas del Acompañante (`CA-01`).
   * Va acá para que el `<Route>` de `App.tsx` se monte: sin este renglón la
   * dirección no llega ni a las rutas.
   */
  SEGMENTOS.situacionesCriticas,
]

/**
 * Si la cola de `#/brazos/<brazo>/…` nombra una pantalla que el brazo sirve.
 *
 * ⛔ Comparación EXACTA, con mayúsculas y todo: ver `PANTALLAS_DEL_BRAZO`.
 *
 * ⚠️ La cola vacía (`#/brazos/<brazo>`) devuelve `false` y **está bien**: esa
 * dirección la redirige `canonicalizar()` antes de llegar acá (`CA-87`), así que
 * nunca se dibuja. ⟦rastro · ADR-0082 · 5/10/2026⟧ · `R4-UIB1`: el destino de esa
 * redirección depende del rol — el Administrador va a Participantes (`CA-87`) y el
 * Acompañante a `…/situaciones-criticas` (`:826-832`). Acá decía «a Participantes»
 * a secas, que ya no vale para los dos.
 */
export function esPantallaDelBrazo(cola: string): boolean {
  return PANTALLAS_DEL_BRAZO.includes(cola)
}

/** El menú del marco transversal. `R1.1-F3-CA-06`: exactamente estos tres. */
export const MENU_TRANSVERSAL: readonly (readonly [ruta: string, etiqueta: string])[] = [
  [INICIO, "Volver al inicio"],
  [RUTA_DEL_BANCO, "Banco de contenido"],
  [RUTA_DE_LA_CONFIGURACION_INICIAL, "Configuración del chatbot"],
]

/**
 * **Las direcciones viejas CON brazo y su destino nuevo.** `PRD_R1.1.md:311` y
 * los criterios `R1.1-F2-CA-70`…`CA-78`.
 *
 * ⚠️ `cargas` es el único que cambia de nombre y no sólo de prefijo
 * (`CA-72`: `#/hibrido/cargas` → `#/brazos/hibrido/participantes/carga-en-lote`).
 */
const RUTAS_VIEJAS_CON_BRAZO: Record<string, string> = {
  participantes: SEGMENTOS.participantes,
  "participantes/alta": SEGMENTOS.alta,
  cargas: SEGMENTOS.cargaEnLote,
  conversaciones: SEGMENTOS.conversaciones,
  plantillas: SEGMENTOS.plantillas,
  envios: SEGMENTOS.envios,
  exportacion: SEGMENTOS.exportacion,
  metricas: SEGMENTOS.metricas,
  simulador: SEGMENTOS.simulador,
}

/**
 * **Las direcciones viejas SIN brazo.** Todas van a Inicio: `PRD_R1.1.md:310` y
 * `R1.1-F2-CA-61`…`CA-69`.
 *
 * ⛔ **No se «arreglan» adivinando un brazo**, que es la implementación que pide
 * la intuición («el operador quería ver participantes»). Sin brazo en la
 * dirección no hay forma de saber CUÁL, y mandarlo al híbrido por defecto es
 * exactamente lo que `PRODUCT.md` §Principios ① prohíbe: dejarlo mirando datos
 * del brazo equivocado con un rótulo que dice otra cosa.
 */
const RUTAS_VIEJAS_SIN_BRAZO: readonly string[] = Object.keys(RUTAS_VIEJAS_CON_BRAZO)

/** El banco, con el nombre viejo. `R1.1-F2-CA-79` y `CA-80`. */
const NOMBRE_VIEJO_DEL_BANCO = "documentos"

/**
 * Los segmentos de una ruta, **sin colapsar los vacíos de adentro**.
 *
 * ⛔⛔ **El vacío del medio es un DATO y no basura.** `R1.1-F2-CA-96` pide que
 * `#/brazos//participantes` muestre «Este brazo no existe en KAI»: un
 * `.filter(s => s !== "")` —que es lo que hace `configuracion-del-chatbot/rutas.ts`,
 * y allá está bien porque sólo busca una cola— convertiría esa dirección en
 * `#/brazos/participantes` y el operador terminaría en un brazo llamado
 * «participantes». La barra final sí se saca, porque `CA-88` pide justamente eso.
 */
export function segmentosDe(ruta: string): readonly string[] {
  if (ruta === "" || ruta === "/") return []
  const sinBarraFinal = ruta.endsWith("/") ? ruta.slice(0, -1) : ruta
  const partes = sinBarraFinal.split("/")
  // La primera es siempre vacía: la ruta arranca con `/`.
  return partes[0] === "" ? partes.slice(1) : partes
}

/** Arma una ruta a partir de sus segmentos. */
function ruta(...segmentos: readonly string[]): string {
  return `/${segmentos.join("/")}`
}

/**
 * ⭐⭐ **LA TABLA DE `PRD_R1.1.md:302-325`, HECHA FUNCIÓN.**
 *
 * Devuelve **a dónde hay que reescribir la barra**, o `null` si la dirección ya
 * es la que corresponde.
 *
 * ⛔ `null` **no quiere decir «válida»**: `#/brazos/xyz/participantes` devuelve
 * `null` —no hay a dónde redirigirla— y lo que le toca es la pantalla de error de
 * `CA-91`. Quién dibuja qué es de `App.tsx`; acá sólo se decide el destino.
 *
 * ⚠️⚠️ **EL ORDEN DE LAS REGLAS ESTÁ ESCRITO, Y HOY NO ES LO QUE LO SOSTIENE.**
 *
 * Este bloque decía: ~~«el orden importa; `participantes/alta` es a la vez una
 * ruta vieja SIN brazo y algo que se parece a `<brazo>/<pantalla>`, y si la regla
 * del brazo corriera primero terminaría en un brazo llamado “participantes”»~~.
 * **Medido el 17/9/2026: es falso hoy** — neutralizar la regla ② deja los 56
 * casos de `pruebas/r11-rutas.test.ts` en verde, porque ⑦ exige dos segmentos y
 * busca la cola exacta, que para `participantes/alta` es `"alta"` y no está en la
 * tabla. Las dos caen en ⑧, con el mismo destino. El detalle entero está en la
 * regla ②, con la condición bajo la cual dejaría de ser redundante.
 *
 * ⇒ el orden se escribe igual, **como la tabla del PRD y no como un guard**:
 * sirve para comparar renglón por renglón con `PRD_R1.1.md:302-325` sin tener que
 * razonar el fallthrough. Que no esté medido va dicho, que es lo que un verde no
 * dice.
 */
export function canonicalizar(
  pathname: string,
  rol: Role,
  brazoDelAcompanante: string | null,
): string | null {
  const partes = segmentosDe(pathname)
  const crudo = destinoDe(partes, rol, brazoDelAcompanante)
  /*
   * ⭐⭐ `R2-UI6` · **EL ÚNICO LUGAR DONDE EL ROL TOCA LA TABLA DE R1.1.**
   * `// ADR-0353` · `CA-F6-28` («el Acompañante no puede volver a Inicio»).
   *
   * Once de las veinte reglas del PRD de R1.1 terminan en `INICIO` —las viejas
   * sin brazo ②, el banco ③, la configuración ④, `#/instancia/<no válida>`,
   * `#/brazos` a secas, y el comodín ⑧—. Para quien no puede estar en Inicio, las
   * once tienen que terminar en SU destino.
   *
   * ⛔ **Se hace acá, en una línea, y NO regla por regla.** Once `if (rol === …)`
   * repartidos por `destinoDe` son once lugares que divergen, y el día que se
   * agregue una regla nueva nadie se acuerda de la doceava. Acá la propiedad es
   * estructural: *lo que iba a Inicio va a donde este rol tiene su inicio*.
   *
   * ⛔ **Y para el Administrador es la IDENTIDAD**: `destinoPorRol("administrador")`
   * ES `INICIO`, así que esta línea no puede cambiarle ni una dirección. El
   * Administrador conserva TODO el comportamiento de R1.1 (A11), y eso no depende
   * de que alguien se acuerde: depende de que la tabla diga lo mismo.
   */
  // ⚠️ `R2-UI19`: sin destino (`null`) no se reescribe hacia ningún lado; quien no
  // tiene a dónde caer ya lo atajó la guardia de `App.tsx` (acceso denegado).
  const destino = crudo === INICIO ? destinoPorRol(rol, brazoDelAcompanante) : crudo
  // Una barra final se saca aunque el destino sea el mismo: `CA-88`.
  const actual = partes.length === 0 ? INICIO : ruta(...partes)
  if (destino === null) return pathname === actual ? null : actual
  return destino === pathname ? null : destino
}

function destinoDe(partes: readonly string[], rol: Role, brazoDelAcompanante: string | null): string | null {
  /*
   * ① `#/` — Inicio.
   *
   * ⚠️ `R2-UI6` · **acá decía `return null` («ya está donde va») y ahora devuelve
   * `INICIO`.** Para el Administrador es idéntico —`canonicalizar` termina en
   * `destino === pathname ? null : destino`, y `"/" === "/"` da el mismo `null`—;
   * la diferencia es que ahora la dirección pasa por el mapeo de rol de arriba,
   * que es lo único que puede cumplir `CA-F6-28`.
   */
  if (partes.length === 0) return INICIO

  const [primera, segunda, ...resto] = partes
  const cola = partes.join("/")

  /*
   * ② Las direcciones viejas SIN brazo → Inicio. `CA-61`…`CA-69`.
   *
   * ⚠️⚠️ **MEDIDO EL 17/9/2026: esta línea NO LA MIDE NINGÚN CASO.**
   * Neutralizándola (`if (false && …)`), los 56 de `pruebas/r11-rutas.test.ts`
   * siguen en verde, porque hoy es redundante con ⑧:
   *
   *   · las ocho viejas de UN segmento (`/metricas`, `/cargas`…) no matchean ⑦,
   *     que exige dos;
   *   · la única de dos —`participantes/alta`— tampoco, porque ⑦ busca la COLA
   *     (`"alta"`) en `RUTAS_VIEJAS_CON_BRAZO`, donde la clave es
   *     `participantes/alta`.
   *
   * ⇒ las dos caen en ⑧ y dan el mismo `INICIO`.
   *
   * ⛔ **Se queda, y no por inercia:** deja de ser redundante el día que
   * `RUTAS_VIEJAS_CON_BRAZO` gane una clave de UN solo segmento que también sea
   * una pantalla vieja —`alta`, por ejemplo—; ahí `#/participantes/alta` se
   * leería como el brazo «participantes» y esta línea sería lo único que lo
   * impide. Y porque `PRD_R1.1.md:310` es un renglón propio de la tabla.
   */
  if (RUTAS_VIEJAS_SIN_BRAZO.includes(cola)) return INICIO

  // ③ El banco con el nombre viejo, con y sin brazo adelante. `CA-79`, `CA-80`.
  if (cola === NOMBRE_VIEJO_DEL_BANCO) return RUTA_DEL_BANCO
  if (partes.length === 2 && segunda === NOMBRE_VIEJO_DEL_BANCO) return RUTA_DEL_BANCO

  // ④ La configuración con el nombre viejo, con y sin brazo adelante. `CA-81`, `CA-82`.
  if (cola === SEGMENTOS.configuracionDelChatbot) return RUTA_DE_LA_CONFIGURACION_INICIAL
  if (partes.length === 2 && segunda === SEGMENTOS.configuracionDelChatbot) {
    return RUTA_DE_LA_CONFIGURACION_INICIAL
  }

  // ⑤ Lo transversal.
  if (primera === SEGMENTOS.instancia) return destinoTransversal(partes)

  // ⑥ Lo del brazo.
  if (primera === SEGMENTOS.brazos) {
    // `#/brazos` sin brazo → Inicio. `CA-86`.
    if (partes.length === 1) return INICIO
    // ⛔ Un brazo que no existe NO se redirige: se le muestra el error. `CA-91`…`CA-97`.
    if (!esBrazo(segunda)) return null
    /*
     * ⭐⭐ `#/brazos/<brazo>` → Participantes. `CA-87` de R1.1 · `CA-F6-33` de F6.
     * `// ADR-0353`
     *
     * **La misma dirección tiene dos desenlaces por rol** (PRD:2173), y éste es el
     * único lugar donde eso se puede escribir:
     *
     *   · Administrador ⇒ Participantes del brazo. `CA-87` **intacto**, que es lo
     *     que `CA-F6-33` pide con todas las letras: *«con la sesión de Admin Uno,
     *     `#/brazos/hibrido` muestra la pantalla de Participantes»*.
     *   · Acompañante ⇒ ⟦rastro · ADR-0082 · 5/10/2026⟧ · `R4-UIB1` (`CA-03`): acá
     *     decía ~~«`null`, se queda donde está. `CA-F6-27`, `CA-F6-28`, `CA-F6-31`:
     *     `#/brazos/hibrido` **es** su pantalla»~~. Eso valía mientras el
     *     Acompañante no tenía pantalla propia; R4-F1 la construye (Situaciones
     *     críticas) y su destino pasa a `<brazo>/situaciones-criticas`. Hoy la
     *     rama de `:823-829` devuelve ese destino y **no** `null`.
     *
     * ⛔ Las dos salidas cómodas rompen un verde cada una (`r2-ui6.md:86`): quitar
     * la redirección para todos rompe `CA-87` y `CA-F6-33`; dejarla para todos
     * rompe `CA-F6-27/28/31`.
     *
     * ⛔ **La condición se le pregunta a `destinoPorRol` y no al nombre del rol**:
     * «¿esta dirección es el destino de este rol?». Un `rol === "acompanante"` acá
     * sería el segundo lugar que sabe dónde cae cada rol.
     */
    if (partes.length === 2) {
      const destino = destinoPorRol(rol, brazoDelAcompanante)
      /*
       * ⭐ `R4-UIB1` · `CA-03`: `#/brazos/<brazo>` redirige SIN ESPERA al marco
       * del rol. Para el Acompañante su destino ya no ES la raíz del brazo sino
       * `<brazo>/situaciones-criticas`, así que se redirige a ese destino.
       *
       * ⛔ Para el Administrador el destino sigue siendo `INICIO`, y ahí NO se
       * toca nada: la redirección a Participantes es `CA-87`/`CA-F6-33` y esta
       * rama la conserva (`A11`). Sólo se desvía cuando el destino del rol
       * cuelga de este brazo.
       */
      if (
        destino !== null &&
        destino !== INICIO &&
        destino.startsWith(`${rutaDelBrazo(segunda)}/`)
      ) {
        return destino
      }
      return rutaDeLaPantalla(segunda, SEGMENTOS.participantes)
    }
    return null
  }

  // ⑦ Las direcciones viejas CON brazo. `CA-70`…`CA-78` y `CA-97`.
  //    ⚠️ El brazo NO se valida acá: `#/xyz/participantes` se redirige a
  //    `#/brazos/xyz/participantes`, que es donde `CA-97` pide que se muestre el
  //    error. Un solo lugar dibuja «Este brazo no existe en KAI».
  if (primera !== undefined && segunda !== undefined) {
    const colaVieja = [segunda, ...resto].join("/")
    const nueva = RUTAS_VIEJAS_CON_BRAZO[colaVieja]
    if (nueva !== undefined) return rutaDeLaPantalla(primera, nueva)
  }

  // ⑧ Cualquier otra cosa → Inicio. `CA-89`.
  return INICIO
}

/**
 * Lo transversal: el banco, la configuración con sus cinco pestañas, y el resto
 * a Inicio (`CA-90`).
 */
function destinoTransversal(partes: readonly string[]): string | null {
  const [, segunda, tercera, cuarta, ...sobra] = partes

  if (segunda === SEGMENTOS.bancoDeContenido) {
    return partes.length === 2 ? null : RUTA_DEL_BANCO
  }

  if (segunda === SEGMENTOS.configuracionDelChatbot) {
    // `#/instancia/configuracion-del-chatbot` → Personalidad. `CA-35` del F3.
    if (tercera === undefined) return RUTA_DE_LA_CONFIGURACION_INICIAL
    // Una pestaña que no existe → Personalidad. `CA-83`.
    if (!(PESTANAS as readonly string[]).includes(tercera)) return RUTA_DE_LA_CONFIGURACION_INICIAL
    if (cuarta === undefined) return sobra.length === 0 ? null : `${RUTA_DE_LA_CONFIGURACION}/${tercera}`
    // `…/mensajes/<mensaje no válido>` → la lista. `CA-84`.
    if (tercera === "mensajes") {
      return (MENSAJES_CON_DIRECCION as readonly string[]).includes(cuarta) && sobra.length === 0
        ? null
        : `${RUTA_DE_LA_CONFIGURACION}/mensajes`
    }
    // `…/guardrails/nueva` es una dirección real; cualquier otra cola vuelve a
    // `…/guardrails`. `PRD_R1.1.md:317` y `:319`.
    if (tercera === "guardrails") {
      return cuarta === "nueva" && sobra.length === 0
        ? null
        : `${RUTA_DE_LA_CONFIGURACION}/guardrails`
    }
    return `${RUTA_DE_LA_CONFIGURACION}/${tercera}`
  }

  // `#/instancia/<no válida>` → Inicio. `CA-90`.
  return INICIO
}
